import {Box, Container, Drawer, IconButton, List, Tooltip} from "../../components";
import {CategoryListItem, CategoryNestListItem} from "./components";
import {MenuListItem, MenuPlatform} from "./types";
import React, {FC, ReactElement, useMemo} from "react";
import {useLocation} from "react-use";
import {Colors} from "../../colors";
import {BounceLogo, BounceMark, IconChevronsLeft} from "../../assets";
import {getMenuPlatformColors} from "./utils";
import {MenuCollapsedContext} from "./MenuCollapsedContext";
import {MenuLogo} from "./MenuLogo";
import {useLocalStorageState} from "../../../hooks";

const drawerWidth = 280;
const collapsedWidth = 64;
const drawerTransition = "width 220ms ease";
const fadeTransition = "opacity 180ms ease";

const getCurrentPage = (pathname: string | undefined, menuItems: MenuListItem[]): string => {
  const ids = menuItems.flatMap((item) =>
    item.type === "category-nest-item" ? item.subListItems.map((s) => s.id) : [item.id]
  );
  return (
    pathname
      ?.split("/")
      .reverse()
      .find((s) => ids.includes(s)) ?? ""
  );
};

type PermanentMenuProps = {
  menuItems: MenuListItem[];
  header?: ReactElement;
  platform: MenuPlatform;
};

const PermanentMenu: FC<PermanentMenuProps> = ({menuItems, header, platform}) => {
  const [collapsed, setCollapsed] = useLocalStorageState<boolean>("permanent-menu-collapsed", false, {
    encrypted: false,
  });
  const {pathname} = useLocation();

  const selected = useMemo(() => getCurrentPage(pathname, menuItems), [pathname, menuItems]);

  const width = collapsed ? collapsedWidth : drawerWidth;
  const {logoColor} = getMenuPlatformColors(platform);

  const toggleButton = (
    <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"} placement="right">
      <IconButton
        onClick={() => setCollapsed((prev) => !prev)}
        disableRipple
        sx={{
          padding: "6px",
          border: `1px solid ${Colors.tertiary._300}`,
          borderRadius: "8px",
        }}
      >
        <IconChevronsLeft
          width={20}
          height={20}
          color={Colors.tertiary._600}
          style={{transform: collapsed ? "scaleX(-1)" : undefined}}
        />
      </IconButton>
    </Tooltip>
  );

  return (
    <MenuCollapsedContext.Provider value={collapsed}>
      <Box sx={{display: "flex", backgroundColor: Colors.tertiary._100}}>
        <Drawer
          sx={{
            width,
            flexShrink: 0,
            transition: drawerTransition,
            "& .MuiDrawer-paper": {
              width: width,
              height: "100vh",
              boxSizing: "border-box",
              backgroundColor: Colors.bw.white,
              padding: collapsed ? "24px 8px" : "24px 16px",
              display: "flex",
              flexDirection: "column",
              boxShadow: "none",
              borderRight: `1px solid ${Colors.tertiary._300}`,
              transition: drawerTransition,
              overflowX: "hidden",
              position: "sticky",
              top: 0,
              "&:hover .permanent-menu-toggle": {opacity: 1, pointerEvents: "auto"},
              ...(collapsed && {"&:hover .permanent-menu-logo": {opacity: 0}}),
            },
          }}
          PaperProps={{
            ...(collapsed && {
              onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                if ((e.target as HTMLElement).closest("button, a, [role='button'], [role='menuitem']")) return;
                setCollapsed(false);
              },
              style: {cursor: "pointer"},
            }),
          }}
          variant="permanent"
          anchor="left"
        >
          <Container
            row
            sx={{
              position: "relative",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "space-between",
              gap: "8px",
              paddingBottom: "32px",
            }}
          >
            <Box className="permanent-menu-logo" sx={{transition: fadeTransition}}>
              <MenuLogo platform={platform} />
            </Box>
            <Box
              className="permanent-menu-toggle"
              sx={{
                opacity: 0,
                pointerEvents: "none",
                transition: fadeTransition,
                ...(collapsed && {
                  position: "absolute",
                  inset: 0,
                  bottom: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }),
              }}
            >
              {toggleButton}
            </Box>
          </Container>
          {header && <Container sx={{paddingBottom: "32px"}}>{header}</Container>}
          <List sx={{flexGrow: 1, padding: "0"}}>
            {menuItems
              .filter((item) => item.display)
              .map((item) =>
                item.type === "category-nest-item" ? (
                  <CategoryNestListItem
                    key={`${item.id}-${collapsed}`}
                    item={item}
                    selected={selected}
                    platform={platform}
                  />
                ) : (
                  <CategoryListItem key={item.id} item={item} selected={selected} platform={platform} />
                )
              )}
          </List>
          <Container sx={{justifyContent: "center", padding: collapsed ? "12px 0" : "12px 28px"}}>
            {collapsed ? (
              <BounceMark width="40px" color={logoColor} />
            ) : (
              <BounceLogo width="200px" height="auto" color={logoColor} />
            )}
          </Container>
        </Drawer>
      </Box>
    </MenuCollapsedContext.Provider>
  );
};

export default PermanentMenu;
